import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {search} from '../../libraries/DnaApi';

const FALLBACK_COVER = '/images/cover-placeholder.jpg';

/**
 * The always-there search: an input with a frosted quick-peek dropdown.
 * Click a row to play it, Enter for the full results page, Esc to clear.
 * `hero` renders the big home version, otherwise the compact header one.
 */
const SearchBar = forwardRef(function SearchBar({hero, onPlay, onResults}, ref) {
    const [q, setQ] = useState('');
    const [hits, setHits] = useState([]);
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);
    const boxRef = useRef(null);
    const seqRef = useRef(0);
    const timerRef = useRef(null);

    useImperativeHandle(ref, () => ({focus: () => inputRef.current?.focus()}));

    useEffect(() => {
        const onDown = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const onInput = (val) => {
        setQ(val);
        setOpen(true);
        clearTimeout(timerRef.current);
        if (val.trim().length < 2) {
            setHits([]);
            return;
        }
        timerRef.current = setTimeout(() => {
            const seq = ++seqRef.current;
            search(encodeURIComponent(val.trim()))
                .then((res) => {
                    if (seq === seqRef.current && res) {
                        setHits(res.hits || []);
                    }
                })
                .catch(() => {});
        }, 140);
    };

    const reset = () => {
        setQ('');
        setHits([]);
        setOpen(false);
        inputRef.current?.blur();
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            reset();
        } else if (e.key === 'Enter' && q.trim().length >= 2) {
            onResults(q.trim());
            reset();
        }
    };

    const showDrop = open && q.trim().length >= 2;

    return (
        <div ref={boxRef} className={'relative w-full ' + (hero ? 'max-w-[620px]' : 'max-w-[460px]')}>
            <div className={'flex items-center gap-3 bg-white/[0.08] backdrop-blur-xl transition focus-within:bg-white/[0.11] '
                + 'shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_18px_44px_rgba(0,0,0,0.35)] '
                + (hero ? 'rounded-2xl px-5 py-4' : 'rounded-xl px-3.5 py-2')}>
                <i className={'fa-solid fa-magnifying-glass text-white/45 ' + (hero ? 'text-[15px]' : 'text-[12px]')}/>
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => onInput(e.target.value)}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={hero ? 'Search any song, artist or album…' : 'Search songs, artists, albums…'}
                    className={'w-full bg-transparent font-medium text-white placeholder-white/30 outline-none '
                        + (hero ? 'text-[17px]' : 'text-[13px]')}
                />
                {q ? (
                    <button type="button" onClick={reset} className="text-white/40 hover:text-white">
                        <i className="fa-solid fa-xmark"/>
                    </button>
                ) : (
                    <span className="hidden flex-none rounded border border-white/15 px-1.5 text-[10px] text-white/30 sm:inline">⌘K</span>
                )}
            </div>

            {showDrop && (
                <div className="absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-2xl bg-[#1c140f]/90 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_30px_70px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
                    {hits.length === 0 ? (
                        <p className="px-3 py-4 text-center text-[12.5px] text-white/40">Nothing matches yet. Keep typing.</p>
                    ) : (
                        <div className="scrollbar-subtle max-h-[60vh] overflow-y-auto">
                            {hits.map((h) => (
                                <button key={h.id} type="button"
                                        onClick={() => { onPlay(h); reset(); }}
                                        className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition hover:bg-white/[0.08]">
                                    <img src={h.cover || FALLBACK_COVER} alt="" loading="lazy"
                                         className="h-10 w-10 flex-none rounded-lg object-cover"/>
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-2 text-[13.5px] font-semibold">
                                            <span className="truncate">{h.title}</span>
                                            {h.is_live && <span className="flex-none rounded-full bg-apple-red/15 px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase text-apple-red">Live</span>}
                                            {h.is_remix && <span className="flex-none rounded-full bg-white/10 px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase text-white/60">Remix</span>}
                                        </span>
                                        <span className="block truncate text-[11.5px] text-white/[0.45]">
                                            {[h.artist, h.album, h.year].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                    <button type="button" onClick={() => { onResults(q.trim()); reset(); }}
                            className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl py-2 text-[12px] font-semibold text-white/50 transition hover:bg-white/[0.06] hover:text-white">
                        See all results <b className="rounded border border-white/15 px-1.5 text-[10px] font-normal">↵</b>
                    </button>
                </div>
            )}
        </div>
    );
});

export default SearchBar;
